import 'highcharts/highcharts-more'; // important to make Highcharts work with chart type "arearange"
import { useAccountStore } from '@/stores/accountStore';
import { truthy } from '@/utils/helper';
import { DatapointDto } from '@/views/TrendsDisplayView/types';
import * as Highcharts from 'highcharts';
import { merge, omit, uniq } from 'lodash';
import { computed, ComputedRef, readonly, ref, Ref, toValue } from 'vue';
import { HighchartsInstance } from './types';
import { Duration } from 'luxon';
import 'highcharts/modules/exporting';
import 'highcharts/modules/export-data';

interface MergeProps {
  options: ComputedRef<Highcharts.Options>;
  showInJobContext: Ref<boolean | undefined>;
  startDate: Ref<string | undefined>;
  endDate: Ref<string | undefined>;
}

export function useMergeWithChartDefaults({
  options,
  showInJobContext,
  startDate,
  endDate,
}: MergeProps) {
  const zooming = ref(false);
  const chartComponent = ref<HighchartsInstance | null>(null);
  const selectedDatapointId = ref<string>('');
  const visibleSeries = ref<string[]>([]);

  function onDatapointClick<D extends DatapointDto>(point: D) {
    selectedDatapointId.value = point.id;
  }

  function zoomOut() {
    chartComponent.value?.chart.zoomOut();
  }

  function syncExtremes(this: Highcharts.Axis, e: Highcharts.AxisSetExtremesEventObject) {
    const thisChart = this.chart;
    zooming.value = Boolean(e.min) || Boolean(e.max);

    if (e.trigger !== 'syncExtremes') {
      Highcharts.charts.forEach((chart) => {
        if (chart !== thisChart && chart?.xAxis[0].setExtremes) {
          chart.xAxis[0].setExtremes(e.min, e.max, undefined, false, {
            trigger: 'syncExtremes',
          });
        }
      });
    }
  }

  function handleSeriesHide(seriesId: string) {
    visibleSeries.value = visibleSeries.value.filter((id) => id !== seriesId);
  }

  function handleSeriesShow(seriesId: string) {
    visibleSeries.value = uniq([...visibleSeries.value, seriesId]);
  }

  function formatDataLabel(this: Highcharts.Point) {
    return this.custom?.commentCount ? `<img src="/comment.svg" width="18" height="18" />` : null;
  }

  function handleChartLoad(this: Highcharts.Chart) {
    const initialVisibleSeries = this.series
      .map((series) => {
        const seriesId = series.options.id as string;

        Highcharts.addEvent(series, 'hide', () => handleSeriesHide(seriesId));
        Highcharts.addEvent(series, 'show', () => handleSeriesShow(seriesId));

        return series.visible ? seriesId : undefined;
      })
      .filter(truthy);

    visibleSeries.value = initialVisibleSeries;
  }

  const trendOptions = computed<Highcharts.Options>(() => ({
    chart: {
      zooming: { type: 'x', resetButton: { theme: { style: { top: '-5000px' } } } },
      panning: { enabled: true, type: 'x' },
      panKey: 'shift',
      events: { load: handleChartLoad },
    },
    legend: { enabled: false },
    xAxis: {
      type: 'datetime',
      title: { text: 'Date' },
      minRange: Duration.fromObject({ days: 3 }).as('seconds'),
      minTickInterval: Duration.fromObject({ days: 1 }).as('seconds'),
      labels: { staggerLines: 2, format: '{value:%Y-%m-%d}' },
      min: startDate.value,
      max: endDate.value,
      // Edge markers stick out of the plot box, where Highcharts drops hover; ignored when min/max are set.
      minPadding: 0.05,
      maxPadding: 0.05,
      events: { setExtremes: syncExtremes },
    },
    plotOptions: {
      series: {
        stickyTracking: false,
        cursor: showInJobContext.value ? undefined : 'pointer',
        findNearestPointBy: 'xy',
        dataLabels: {
          crop: false,
          overflow: 'allow',
          enabled: true,
          useHTML: true,
          formatter: formatDataLabel,
        },
      },
    },
  }));

  const targetOptions = computed(() => {
    const optionsValue = toValue(options);
    const optionsWithoutSeries = omit(optionsValue, ['series']);
    const mergedOptions = merge({}, baseChartOptions(), trendOptions.value, optionsWithoutSeries);
    mergedOptions.series = optionsValue.series;
    return mergedOptions;
  });

  return {
    targetOptions,
    zooming: readonly(zooming),
    chartComponent,
    zoomOut,
    onDatapointClick,
    selectedDatapointId,
    visibleSeries,
  };
}

export function baseChartOptions(): Highcharts.Options {
  return {
    lang: { locale: useAccountStore().language },
    title: { text: '' },
    credits: { enabled: false },
    exporting: { enabled: false },
    chart: {
      backgroundColor: 'transparent',
      style: { fontFamily: 'Noto Sans Display Variable' },
    },
    tooltip: { outside: true, useHTML: true, hideDelay: 300 },
  };
}

export const highchartColors = [
  '#7cb5ec', // Blau
  '#434348', // Dunkelgrau
  //'#90ed7d', // Grün
  //'#f7a35c', // Orange
  '#8085e9', // Violett
  '#f15c80', // Pink
  '#f5a623', // Orange

  '#2b908f', // Türkis
  //'#f45b5b', // Rot
  //'#91e8e1', // Helltürkis
  '#e210f5', // pink
];

function generateHighchartSymbols(
  height: number,
): { key: string; symbolSvg: string; symbolChar: string }[] {
  const half = height / 2;

  return [
    {
      key: 'circle',
      symbolSvg: `M${half},0 A${half},${half} 0 1,0 ${half + 0.01},0 Z`,
      symbolChar: '●',
    },
    {
      key: 'square',
      symbolSvg: `M0,0 H${height} V${height} H0 Z`,
      symbolChar: '■',
    },
    {
      key: 'diamond',
      symbolSvg: `M0,${half} L${half},0 L${height},${half} L${half},${height} Z`,
      symbolChar: '◆',
    },
    {
      key: 'triangle',
      symbolSvg: `M${half},0 L${height},${height} H0 Z`,
      symbolChar: '⏶',
    },
    {
      key: 'triangle-down',
      symbolSvg: `M0,0 H${height} L${half},${height} Z`,
      symbolChar: '⏷',
    },
  ];
}

export const highchartSymbols = generateHighchartSymbols(12);

Highcharts.setOptions({
  colors: highchartColors,
  accessibility: {
    enabled: false,
  },
});
